import { ref, computed } from 'vue'
import { THEMES, DEFAULT_THEME, isThemeId, getTheme, type ThemeId } from '@/config/themes'

// Keep in sync with the blocking script in index.html (prevents a flash of
// the wrong theme before Vue mounts).
const STORAGE_KEY = 'turtledove-theme'

// Pre-multi-theme storage key. Only consulted once, on first load, so an
// existing user's light/dark choice carries over instead of silently
// resetting to 'light'.
const LEGACY_DARK_KEY = 'turtledove-dark-mode'

// Shared reactive state
const themeId = ref<ThemeId>(DEFAULT_THEME)

let initialized = false

function applyTheme() {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  root.setAttribute('data-theme', themeId.value)
  root.classList.toggle('dark', getTheme(themeId.value).isDark)
}

function resolveInitialTheme(): ThemeId {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (isThemeId(stored)) return stored

  const legacyDark = localStorage.getItem(LEGACY_DARK_KEY)
  if (legacyDark !== null) return legacyDark === 'true' ? 'dark' : 'light'

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function initialize() {
  if (initialized) return
  initialized = true

  themeId.value = resolveInitialTheme()
  applyTheme()
}

function setTheme(id: ThemeId) {
  themeId.value = id
  localStorage.setItem(STORAGE_KEY, id)
  applyTheme()
}

export function useTheme() {
  // Initialize on first use
  initialize()

  return {
    themeId,
    themes: THEMES,
    isDark: computed(() => getTheme(themeId.value).isDark),
    setTheme,
    /** Binary compatibility helper for call sites that only care about light/dark. */
    toggleDark: () => setTheme(getTheme(themeId.value).isDark ? 'light' : 'dark'),
  }
}
